'use client'

import type { TreeNodeType } from '@/shared/lib/tree'
import type { FieldViewProps, Localization } from '../../types'
import type TreeSelectController from './Controller'

import React, { useMemo, useState } from 'react'
import { ListTreeIcon } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { BadgeOverflow } from '@/shared/components/ui/badge-overflow'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseControl,
	InputBaseFlexWrapper,
} from '@/shared/components/ui/input-base'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { findNodes, getIsAllSelected } from '@/shared/lib/tree'

import TreePicker from '../../components/TreePicker'
import { stringToArray } from '../../utils/utils'

type Props = FieldViewProps<TreeSelectController> & {
	locale: Localization
}
export default function TreeSelectView({
	field,
	storedValue,
	onChange,
	invalidMessage,
	disabled,
	locale,
}: Props) {
	const { operation } = storedValue
	const { useSelectOptions } = field
	const selectOptions: any = useSelectOptions()
	const [open, setOpen] = useState(false)
	const options: TreeNodeType[] = useMemo(
		() => selectOptions.data || [],
		[selectOptions.data]
	)
	const value: string[] = useMemo(
		() => stringToArray(storedValue?.value || []),
		[storedValue]
	)
	const isInvalid = Boolean(invalidMessage)
	const handleChange = (value: string[]) => onChange({ operation, value })

	return (
		<div className='col-span-3'>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild className='w-full' disabled={disabled}>
					<InputBase
						asChild
						role='button'
						aria-expanded={open}
						aria-invalid={isInvalid}
						data-testid='query-builder-value-tree'
						disabled={disabled}
					>
						<button>
							<InputBaseControl>
								<InputBaseFlexWrapper className='w-full truncate'>
									{value?.length > 0 ? (
										<div className='flex w-full flex-col'>
											<BadgeOverflow
												items={formatState?.(storedValue.value, options)}
												renderBadge={(_, label) => (
													<Badge variant='default'>{label}</Badge>
												)}
												renderOverflow={(count) => (
													<Badge variant='outline' className='bg-muted'>
														+{count}
													</Badge>
												)}
											/>
										</div>
									) : (
										field.formatLabel(value)
									)}
								</InputBaseFlexWrapper>
							</InputBaseControl>
							<InputBaseAdornment>
								<ListTreeIcon className='ml-2 size-4 shrink-0 opacity-50' />
							</InputBaseAdornment>
						</button>
					</InputBase>
				</PopoverTrigger>
				<PopoverContent className='flex max-h-[300px] w-auto min-w-(--radix-popover-trigger-width) flex-col overflow-hidden p-0'>
					<TreePicker
						value={value}
						onChange={handleChange}
						tree={options}
						locale={locale}
					/>
				</PopoverContent>
			</Popover>
		</div>
	)
}

const formatState = (value: string[], options?: TreeNodeType[]) => {
	const output: string[] = []
	const _value = stringToArray(value || '')
	const scan = (node: TreeNodeType) => {
		if (!node.children.length && _value.includes(node.id)) {
			output.push(node.name)
			return
		}
		if (node.children.length > 0) {
			if (getIsAllSelected(node.children, _value)) {
				output.push(node.name)
				return
			} else {
				for (const child of node.children) {
					scan(child)
				}
			}
		}
	}
	for (const node of options || []) {
		scan(node)
	}

	return output
}
